<template>
  <ProgressCircular v-if="loading" />
  <form v-else novalidate :disabled="disabled" @submit.prevent="submit()">
    <v-row no-gutters>
      <v-col cols="8">
        <v-card max-width="1200" flat color="transparent">
          <v-card-title class="mb-10">
            <FormHeader
              :loading="loading"
              :disabled="disabled"
              :link="'page-streams'"
              :title="getTitle ? `Редактировать ${getTitle}` : `Добавить трансляцию`"
            />
          </v-card-title>
          <v-card-text>
            <PageContent height="85">
              <v-container>
                <!-- Stream Name -->
                <v-row>
                  <v-col cols="12">
                    <span>Имя * (<TranslationIcon />)</span>
                    <v-text-field
                      outlined
                      id="name"
                      :autofocus="!fields.title"
                      :error-messages="validationErrors.title"
                      :disabled="disabled"
                      :loading="loading"
                      v-model="fields.title"
                    />
                  </v-col>
                </v-row>
                <!-- is Active -->
                <v-row>
                  <v-col cols="6">
                    <v-switch
                      outlined
                      label="Активен"
                      :disabled="disabled"
                      :loading="loading"
                      v-model="fields.isActive"
                    />
                  </v-col>
                </v-row>
                <!-- is Online -->
                <v-row>
                  <v-col cols="6">
                    <v-switch
                      outlined
                      label="Онлайн"
                      :disabled="disabled"
                      :loading="loading"
                      v-model="fields.isOnline"
                    />
                  </v-col>
                </v-row>
                <MediaFilePicker
                  :label="'Изображение'"
                  :multiple="false"
                  :file-type="$options.mediaTypesByKeys.IMAGE"
                  :value="fields.thumbnailId"
                  @change="fields.thumbnailId = $event"
                />
                <v-row v-if="$apollo.queries.streamsExternal.loading">
                  <ProgressCircular />
                </v-row>
                <template v-else>
                  <v-row>
                    <v-col cols="6">
                      <span>Выберите поток камеры</span>
                      <v-select
                        outlined
                        no-data-text="Список пуст"
                        item-value="id"
                        item-text="name"
                        clearable
                        disable-lookup
                        :loading="loading || $apollo.queries.streamsExternal.loading"
                        :disabled="disabled"
                        :value="fields.streamId"
                        :items="streamsExternal"
                        @input="fields.streamId = $event"
                      >
                        <template v-slot:item="{ item }">
                          <v-list>
                            <v-list-item>
                              <v-subheader>Имя: {{ item.name }}</v-subheader>
                              <v-list-item-group>
                                <v-list-item-content>
                                  <v-list-item-subtitle
                                    >Город: {{ item.districtName }}</v-list-item-subtitle
                                  >
                                  <v-list-item-subtitle
                                    >Тип: {{ item.cameraTypeName }}</v-list-item-subtitle
                                  >
                                </v-list-item-content>
                              </v-list-item-group>
                            </v-list-item>
                          </v-list>
                        </template>
                      </v-select>
                    </v-col>
                  </v-row>
                  <!-- Location -->
                  <v-row v-if="fields.streamId">
                    <v-col cols="12">
                      <span>Расположение</span>
                      <LocationAutocomplete
                      ref="LocationAutocomplete"
                        :address="getAddress"
                        readonly
                        :loading="loading || disabled"
                        @set-location="setLocation"
                        @update-location-part="updateLocation"
                      />
                    </v-col>
                  </v-row>
                </template>
              </v-container>
            </PageContent>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="4">
        <ManageSidePanel
          isShowSortOrder
          :isNew="isNew"
          :created-at="getCreatedAt"
          :updated-at="getUpdatedAt"
          :language="language"
          :is-submit-disabled="disabled"
          :is-loading="loading"
          :translations="fields.translations"
          :entity-id="getId"
          :entity-type="'stream'"
          :sortOrder="fields.sortOrder"
          @delete="$emit('delete-stream')"
          @cancel="$router.push({ name: 'page-streams' })"
          @update-translations="Object.assign(fields.translations, $event)"
          @language-update="$emit('language-update', $event)"
          @update-sort-order="fields.sortOrder = Number($event)"
        >
          <template #body>
            <v-col cols="12">
              <CityPicker title="Город" v-model="fields.cityId" />
            </v-col>
            <v-col cols="12">
              <PlacePicker title="Место" v-model="fields.placeId" />
            </v-col>
          </template>
        </ManageSidePanel>
      </v-col>
    </v-row>
  </form>
</template>

<script>
import validationTranslations from '@/mixins/translations/validationTranslations.mix.js'
import prepareTranslations from '@/mixins/translations/prepareTranslations.mix'

import { defaultDateFormat } from '@/configs/date.js'
import Geolocation from '@/utils/types/Geolocation.js'
import tabs from '@/configs/tabs.js'
import { mediaTypesByKeys } from '@/configs/mediaFileTypes'
import validationRules from './validation-rules'

import FormAbstract from '@/components/abstract/form'
import LocationAutocomplete from '@/components/location-autocomplete'
import ManageSidePanel from '@/components/manage-side-panel'
import CityPicker from '@/components/city-picker'
import PlacePicker from '@/components/place-picker/PlacePicker.vue'
import TranslationIcon from '@/components/translation-icon/translationIcon'
import FormHeader from '@/components/form-header'
import PageContent from '@/components/page-content/PageContent.vue'
import ProgressCircular from '@/components/progress-circular'
import MediaFilePicker from '@/components/media-file-picker'

import streamsExternal from '@/services/gql/types/StreamsExternal/queries/streamsExternal.gql'

export default {
  name: 'FormStream',
  tabs,
  defaultDateFormat,
  validationRules,
  mediaTypesByKeys: mediaTypesByKeys(),
  extends: FormAbstract,
  mixins: [validationTranslations, prepareTranslations],
  components: {
    PageContent,
    TranslationIcon,
    ManageSidePanel,
    LocationAutocomplete,
    CityPicker,
    PlacePicker,
    FormHeader,
    ProgressCircular,
    MediaFilePicker
  },
  props: {
    data: {
      type: Object,
      default: null
    },
    isNew: {
      type: Boolean,
      default: true
    },
    language: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      streamsExternal: null,
      fields: {
        title: '',
        isActive: false,
        isOnline: false,
        location: new Geolocation(),
        thumbnailId: null,
        cityId: null,
        placeId: null,
        streamId: null,
        translations: { ru: { isEnabled: true } }
      }
    }
  },
  computed: {
    getTitle () {
      if (this.data) {
        return this.data?.title || ''
      }
      return null
    },
    getAddress () {
      if (!this.fields.streamId) return this.fields.location
      const { lat, lng, address } =
        this.streamsExternal?.find(stream => stream.id === this.fields.streamId) || {}
      return {
        lat: lng,
        address,
        lon: lat,
        displayAddress: this.fields.location?.displayAddress || address || ''
      }
    },
    getCreatedAt () {
      return this.data?.createdAt || null
    },
    getUpdatedAt () {
      return this.data?.updatedAt || null
    },
    getId () {
      return this.data?.id || null
    }
  },
  watch: {
    data (data) {
      this.populate(data)
      this.fields.cityId = data?.city?.id || null
      this.fields.placeId = data?.place?.id || null
      this.$set(this.fields, 'thumbnailId', data?.thumbnail?.id || null)
    }
  },
  methods: {
    setLocation (location) {
      this.fields.location = location
    },
    updateLocation ({ value, fieldName }) {
      this.fields.location[fieldName] = value
    },
    async submit () {
      const isValid = await this.validate()
      if (!isValid) {
        this.mixScrollToError(this.$v)
        return
      }
      if (!this.mixIsValidTranslations(this.fields.translations)) {
        this.$errorMsg('Не выбран ни один язык для контента!')
        return
      }
      const payload = {
        ...this.composeFormData(this.fields)
      }
      payload.location = this.getAddress
      this.mixPrepareTranslations(payload)
      this.$emit('submit', payload)
    },
    getStatusStreamsExternal (status) {
      return Number(status) === 0
    }
  },
  apollo: {
    streamsExternal: {
      query: streamsExternal,
      fetchPolicy: 'network-only',
      pollInterval: 30000,
      variables () {
        return {
          where: {}
        }
      },
      result (res) {
        this.streamsExternal = res?.data?.streamsExternal?.list
      }
    }
  }
}
</script>
